"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tree = exports.ROOT_ID = void 0;
const TreeNode_1 = require("./TreeNode");
const BidirectionalMap_1 = require("../BidirectionalMap");
const errors_1 = require("./errors");
exports.ROOT_ID = "0";
class Tree {
    /** Tree nodes indexed by id. */
    nodes = new Map();
    /**
     * Parent id to child id index.
     * Allows lookups of children by name or child id.
     */
    children = new Map();
    constructor() {
        this.createRootNode();
    }
    /** Create a new root node. */
    createRootNode() {
        const root = new TreeNode_1.ImmutableDirectory({ id: exports.ROOT_ID, type: TreeNode_1.NodeType.Directory }, this);
        this.nodes.set(exports.ROOT_ID, root);
    }
    /** The workspace's root directory. */
    get root() {
        return this.nodes.get(exports.ROOT_ID);
    }
    /** Get a node by its id. */
    getNodeById(id) {
        return this.nodes.get(id);
    }
    /** Get a node's id with the given path. */
    getIdFromPath(path) {
        const parts = path.split("/");
        // We start at root so remove first slash...
        if (!parts[0]) {
            parts.shift();
        }
        const baseName = parts.pop();
        // If the path is just a slash, we're at the root.
        if (!baseName && parts.length === 0)
            return this.root.id;
        // If there's a trailing slash, this is an invalid path.
        if (!baseName)
            return undefined;
        // Traverse tree until last dir
        let node = this.root;
        for (const part of parts) {
            if (!node.isDirNode()) {
                return undefined;
            }
            node = node.getChildByName(part);
            if (!node) {
                return undefined;
            }
        }
        // Get the actual node...
        if (!node.isDirNode()) {
            return undefined;
        }
        return node.getChildByName(baseName)?.id;
    }
    /** Get a node with the given path. */
    getNodeFromPath(path) {
        const id = this.getIdFromPath(path);
        if (id) {
            return this.getNodeById(id);
        }
    }
    /** Move the provided node id to the given parent id. */
    moveNode(id, parentId, name) {
        const node = this.nodes.get(id);
        (0, errors_1.nodeExists)(node, `Attempting to move non-existent node: ${id}.`);
        this._removeChild(node);
        node.parentId = parentId;
        node.name = name ?? node.name;
        this._addChild(node, parentId);
        return node;
    }
    /** Delete the node with the given id. */
    deleteNode(id) {
        const node = this.nodes.get(id);
        (0, errors_1.nodeExists)(node, `Attempting to delete non-existent node: ${id}.`);
        this._removeNode(node);
    }
    /** Add a node to the tree. */
    _addNode(node) {
        const parentNode = node.parent;
        if (parentNode && parentNode.getChildByName(node.name)) {
            throw new Error(`${node.name} already exists in directory ${parentNode.name ?? "/"}.`);
        }
        this.nodes.set(node.id, node);
        if (node.isImmutableDirectory())
            return;
        this._addChild(node, node.parentId);
    }
    /** Add JSON Nodes to the tree. */
    _addJSONNodes(nodes) {
        for (const node of nodes) {
            this._addJSONNode(node);
        }
    }
    /** Add a node to the tree. */
    _addJSONNode(node) {
        this._addNode((0, TreeNode_1.createNode)(node, this));
    }
    /** Add a parent-child relation to the tree. */
    _addChild(node, parentId) {
        if (node.isImmutableDirectory())
            return;
        let childrenMap = this.children.get(parentId);
        if (!childrenMap) {
            childrenMap = new BidirectionalMap_1.BidirectionalMap();
            this.children.set(parentId, childrenMap);
        }
        childrenMap.set(node.id, node.name);
    }
    /** Recursively remove a node and all its children. */
    _removeNode(node) {
        if (node.isImmutableDirectory()) {
            throw new Error("Cannot remove the root node");
        }
        if (node.isDirNode()) {
            node.children.forEach((child) => this._removeNode(child));
        }
        this.nodes.delete(node.id);
        this._removeChild(node);
    }
    /** Remove a node's parent-child relation from the tree. */
    _removeChild(node) {
        if (node.isImmutableDirectory())
            return;
        const children = this.children.get(node.parentId);
        if (!children)
            return;
        children.deleteByKey(node.id);
        if (children.size === 0)
            this.children.delete(node.parentId);
    }
    /** Serialize a Tree to JSON. */
    toJSON() {
        const res = [];
        for (const node of this.nodes.values()) {
            res.push({
                id: node.id,
                type: node.type,
                name: node.name,
                parentId: node.parentId,
            });
        }
        return res;
    }
    /** Deserialize a Tree from JSON. */
    static fromJSON(nodes) {
        const tree = new Tree();
        tree._addJSONNodes(nodes);
        return tree;
    }
}
exports.Tree = Tree;
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiVHJlZS5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uL3NyYy9CZWRyb2NrRlMvVHJlZS50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiOzs7QUFBQSx5Q0FNb0I7QUFDcEIsMERBQXVEO0FBQ3ZELHFDQUFzQztBQUd6QixRQUFBLE9BQU8sR0FBRyxHQUFTLENBQUM7QUFJakMsTUFBYSxJQUFJO0lBQ2YsZ0NBQWdDO0lBQ2hDLEtBQUssR0FBc0IsSUFBSSxHQUFHLEVBQUUsQ0FBQztJQUNyQzs7O09BR0c7SUFDSCxRQUFRLEdBQTBDLElBQUksR0FBRyxFQUFFLENBQUM7SUFFNUQ7UUFDRSxJQUFJLENBQUMsY0FBYyxFQUFFLENBQUM7SUFDeEIsQ0FBQztJQUVELDhCQUE4QjtJQUN0QixjQUFjO1FBQ3BCLE1BQU0sSUFBSSxHQUFHLElBQUksNkJBQWtCLENBQ2pDLEVBQUUsRUFBRSxFQUFFLGVBQU8sRUFBRSxJQUFJLEVBQUUsbUJBQVEsQ0FBQyxTQUFTLEVBQUUsRUFDekMsSUFBSSxDQUNMLENBQUM7UUFDRixJQUFJLENBQUMsS0FBSyxDQUFDLEdBQUcsQ0FBQyxlQUFPLEVBQUUsSUFBSSxDQUFDLENBQUM7SUFDaEMsQ0FBQztJQUVELHNDQUFzQztJQUN0QyxJQUFJLElBQUk7UUFDTixPQUFPLElBQUksQ0FBQyxLQUFLLENBQUMsR0FBRyxDQUFDLGVBQU8sQ0FBd0IsQ0FBQztJQUN4RCxDQUFDO0lBRUQsNEJBQTRCO0lBQzVCLFdBQVcsQ0FBQyxFQUFNO1FBQ2hCLE9BQU8sSUFBSSxDQUFDLEtBQUssQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDLENBQUM7SUFDNUIsQ0FBQztJQUVELDJDQUEyQztJQUMzQyxhQUFhLENBQUMsSUFBWTtRQUN4QixNQUFNLEtBQUssR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLEdBQUcsQ0FBQyxDQUFDO1FBRTlCLDRDQUE0QztRQUM1QyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUMsQ0FBQyxFQUFFLENBQUM7WUFDZCxLQUFLLENBQUMsS0FBSyxFQUFFLENBQUM7UUFDaEIsQ0FBQztRQUVELE1BQU0sUUFBUSxHQUFHLEtBQUssQ0FBQyxHQUFHLEVBQUUsQ0FBQztRQUM3QixrREFBa0Q7UUFDbEQsSUFBSSxDQUFDLFFBQVEsSUFBSSxLQUFLLENBQUMsTUFBTSxLQUFLLENBQUM7WUFBRSxPQUFPLElBQUksQ0FBQyxJQUFJLENBQUMsRUFBRSxDQUFDO1FBQ3pELHdEQUF3RDtRQUN4RCxJQUFJLENBQUMsUUFBUTtZQUFFLE9BQU8sU0FBUyxDQUFDO1FBRWhDLCtCQUErQjtRQUMvQixJQUFJLElBQUksR0FBeUIsSUFBSSxDQUFDLElBQUksQ0FBQztRQUMzQyxLQUFLLE1BQU0sSUFBSSxJQUFJLEtBQUssRUFBRSxDQUFDO1lBQ3pCLElBQUksQ0FBQyxJQUFJLENBQUMsU0FBUyxFQUFFLEVBQUUsQ0FBQztnQkFDdEIsT0FBTyxTQUFTLENBQUM7WUFDbkIsQ0FBQztZQUNELElBQUksR0FBRyxJQUFJLENBQUMsY0FBYyxDQUFDLElBQUksQ0FBQyxDQUFDO1lBQ2pDLElBQUksQ0FBQyxJQUFJLEVBQUUsQ0FBQztnQkFDVixPQUFPLFNBQVMsQ0FBQztZQUNuQixDQUFDO1FBQ0gsQ0FBQztRQUVELHlCQUF5QjtRQUN6QixJQUFJLENBQUMsSUFBSSxDQUFDLFNBQVMsRUFBRSxFQUFFLENBQUM7WUFDdEIsT0FBTyxTQUFTLENBQUM7UUFDbkIsQ0FBQztRQUNELE9BQU8sSUFBSSxDQUFDLGNBQWMsQ0FBQyxRQUFRLENBQUMsRUFBRSxFQUFFLENBQUM7SUFDM0MsQ0FBQztJQUVELHNDQUFzQztJQUN0QyxlQUFlLENBQUMsSUFBWTtRQUMxQixNQUFNLEVBQUUsR0FBRyxJQUFJLENBQUMsYUFBYSxDQUFDLElBQUksQ0FBQyxDQUFDO1FBQ3BDLElBQUksRUFBRSxFQUFFLENBQUM7WUFDUCxPQUFPLElBQUksQ0FBQyxXQUFXLENBQUMsRUFBRSxDQUFDLENBQUM7UUFDOUIsQ0FBQztJQUNILENBQUM7SUFFRCx3REFBd0Q7SUFDeEQsUUFBUSxDQUFDLEVBQU0sRUFBRSxRQUFZLEVBQUUsSUFBYTtRQUMxQyxNQUFNLElBQUksR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsQ0FBQztRQUNoQyxJQUFBLG1CQUFVLEVBQUMsSUFBSSxFQUFFLHlDQUF5QyxFQUFFLEdBQUcsQ0FBQyxDQUFDO1FBRWpFLElBQUksQ0FBQyxZQUFZLENBQUMsSUFBSSxDQUFDLENBQUM7UUFDeEIsSUFBSSxDQUFDLFFBQVEsR0FBRyxRQUFRLENBQUM7UUFDekIsSUFBSSxDQUFDLElBQUksR0FBRyxJQUFJLElBQUksSUFBSSxDQUFDLElBQUksQ0FBQztRQUM5QixJQUFJLENBQUMsU0FBUyxDQUFDLElBQUksRUFBRSxRQUFRLENBQUMsQ0FBQztRQUMvQixPQUFPLElBQUksQ0FBQztJQUNkLENBQUM7SUFFRCx5Q0FBeUM7SUFDekMsVUFBVSxDQUFDLEVBQU07UUFDZixNQUFNLElBQUksR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUMsQ0FBQztRQUNoQyxJQUFBLG1CQUFVLEVBQUMsSUFBSSxFQUFFLDJDQUEyQyxFQUFFLEdBQUcsQ0FBQyxDQUFDO1FBQ25FLElBQUksQ0FBQyxXQUFXLENBQUMsSUFBSSxDQUFDLENBQUM7SUFDekIsQ0FBQztJQUVELDhCQUE4QjtJQUM5QixRQUFRLENBQUMsSUFBYztRQUNyQixNQUFNLFVBQVUsR0FBRyxJQUFJLENBQUMsTUFBTSxDQUFDO1FBQy9CLElBQUksVUFBVSxJQUFJLFVBQVUsQ0FBQyxjQUFjLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUM7WUFDdkQsTUFBTSxJQUFJLEtBQUssQ0FDYixHQUFHLElBQUksQ0FBQyxJQUFJLGdDQUFnQyxVQUFVLENBQUMsSUFBSSxJQUFJLEdBQUcsR0FBRyxDQUN0RSxDQUFDO1FBQ0osQ0FBQztRQUNELElBQUksQ0FBQyxLQUFLLENBQUMsR0FBRyxDQUFDLElBQUksQ0FBQyxFQUFFLEVBQUUsSUFBSSxDQUFDLENBQUM7UUFDOUIsSUFBSSxJQUFJLENBQUMsb0JBQW9CLEVBQUU7WUFBRSxPQUFPO1FBQ3hDLElBQUksQ0FBQyxTQUFTLENBQUMsSUFBSSxFQUFFLElBQUksQ0FBQyxRQUFRLENBQUMsQ0FBQztJQUN0QyxDQUFDO0lBRUQsa0NBQWtDO0lBQ2xDLGFBQWEsQ0FBQyxLQUFpQjtRQUM3QixLQUFLLE1BQU0sSUFBSSxJQUFJLEtBQUssRUFBRSxDQUFDO1lBQ3pCLElBQUksQ0FBQyxZQUFZLENBQUMsSUFBSSxDQUFDLENBQUM7UUFDMUIsQ0FBQztJQUNILENBQUM7SUFFRCw4QkFBOEI7SUFDOUIsWUFBWSxDQUFDLElBQWM7UUFDekIsSUFBSSxDQUFDLFFBQVEsQ0FBQyxJQUFBLHFCQUFVLEVBQUMsSUFBSSxFQUFFLElBQUksQ0FBQyxDQUFDLENBQUM7SUFDeEMsQ0FBQztJQUVELCtDQUErQztJQUMvQyxTQUFTLENBQUMsSUFBYyxFQUFFLFFBQVk7UUFDcEMsSUFBSSxJQUFJLENBQUMsb0JBQW9CLEVBQUU7WUFBRSxPQUFPO1FBQ3hDLElBQUksV0FBVyxHQUFHLElBQUksQ0FBQyxRQUFRLENBQUMsR0FBRyxDQUFDLFFBQVEsQ0FBQyxDQUFDO1FBQzlDLElBQUksQ0FBQyxXQUFXLEVBQUUsQ0FBQztZQUNqQixXQUFXLEdBQUcsSUFBSSxtQ0FBZ0IsRUFBRSxDQUFDO1lBQ3JDLElBQUksQ0FBQyxRQUFRLENBQUMsR0FBRyxDQUFDLFFBQVEsRUFBRSxXQUFXLENBQUMsQ0FBQztRQUMzQyxDQUFDO1FBQ0QsV0FBVyxDQUFDLEdBQUcsQ0FBQyxJQUFJLENBQUMsRUFBRSxFQUFFLElBQUksQ0FBQyxJQUFJLENBQUMsQ0FBQztJQUN0QyxDQUFDO0lBRUQsc0RBQXNEO0lBQ3RELFdBQVcsQ0FBQyxJQUFjO1FBQ3hCLElBQUksSUFBSSxDQUFDLG9CQUFvQixFQUFFLEVBQUUsQ0FBQztZQUNoQyxNQUFNLElBQUksS0FBSyxDQUFDLDZCQUE2QixDQUFDLENBQUM7UUFDakQsQ0FBQztRQUVELElBQUksSUFBSSxDQUFDLFNBQVMsRUFBRSxFQUFFLENBQUM7WUFDckIsSUFBSSxDQUFDLFFBQVEsQ0FBQyxPQUFPLENBQUMsQ0FBQyxLQUFLLEVBQUUsRUFBRSxDQUFDLElBQUksQ0FBQyxXQUFXLENBQUMsS0FBSyxDQUFDLENBQUMsQ0FBQztRQUM1RCxDQUFDO1FBRUQsSUFBSSxDQUFDLEtBQUssQ0FBQyxNQUFNLENBQUMsSUFBSSxDQUFDLEVBQUUsQ0FBQyxDQUFDO1FBQzNCLElBQUksQ0FBQyxZQUFZLENBQUMsSUFBSSxDQUFDLENBQUM7SUFDMUIsQ0FBQztJQUVELDJEQUEyRDtJQUMzRCxZQUFZLENBQUMsSUFBYztRQUN6QixJQUFJLElBQUksQ0FBQyxvQkFBb0IsRUFBRTtZQUFFLE9BQU87UUFDeEMsTUFBTSxRQUFRLEdBQUcsSUFBSSxDQUFDLFFBQVEsQ0FBQyxHQUFHLENBQUMsSUFBSSxDQUFDLFFBQVEsQ0FBQyxDQUFDO1FBQ2xELElBQUksQ0FBQyxRQUFRO1lBQUUsT0FBTztRQUN0QixRQUFRLENBQUMsV0FBVyxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUMsQ0FBQztRQUM5QixJQUFJLFFBQVEsQ0FBQyxJQUFJLEtBQUssQ0FBQztZQUFFLElBQUksQ0FBQyxRQUFRLENBQUMsTUFBTSxDQUFDLElBQUksQ0FBQyxRQUFRLENBQUMsQ0FBQztJQUMvRCxDQUFDO0lBRUQsZ0NBQWdDO0lBQ2hDLE1BQU07UUFDSixNQUFNLEdBQUcsR0FBRyxFQUFFLENBQUM7UUFDZixLQUFLLE1BQU0sSUFBSSxJQUFJLElBQUksQ0FBQyxLQUFLLENBQUMsTUFBTSxFQUFFLEVBQUUsQ0FBQztZQUN2QyxHQUFHLENBQUMsSUFBSSxDQUFDO2dCQUNQLEVBQUUsRUFBRSxJQUFJLENBQUMsRUFBRTtnQkFDWCxJQUFJLEVBQUUsSUFBSSxDQUFDLElBQUk7Z0JBQ2YsSUFBSSxFQUFFLElBQUksQ0FBQyxJQUFJO2dCQUNmLFFBQVEsRUFBRSxJQUFJLENBQUMsUUFBUTthQUN4QixDQUFDLENBQUM7UUFDTCxDQUFDO1FBQ0QsT0FBTyxHQUFpQixDQUFDO0lBQzNCLENBQUM7SUFFRCxvQ0FBb0M7SUFDcEMsTUFBTSxDQUFDLFFBQVEsQ0FBQyxLQUFpQjtRQUMvQixNQUFNLElBQUksR0FBRyxJQUFJLElBQUksRUFBRSxDQUFDO1FBQ3hCLElBQUksQ0FBQyxhQUFhLENBQUMsS0FBSyxDQUFDLENBQUM7UUFDMUIsT0FBTyxJQUFJLENBQUM7SUFDZCxDQUFDO0NBQ0Y7QUE1S0Qsb0JBNEtDIn0=